import React, { useState, useEffect, useMemo } from 'react';
import { MessageSquare, Search, Eye, X, User, Mail, Phone, Calendar } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/common/DataTable';
import { ColumnDef } from '@tanstack/react-table';
import { useGetFeedbacksQuery, Feedback } from '@/store/api/feedbackApi';
import { useNewDebounce } from '@/hooks/useNewDebounce';
import Loader from '@/components/Loader';
import { cn } from '@/utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
interface StatCardProps {
  label: string;
  value: string | number;
  icon: React.ComponentType<{ className?: string }>;
}

function StatCard({ label, value, icon: Icon }: StatCardProps) {
  return (
    <div
      className="
        w-[250px] min-w-[250px] max-w-[250px]
        h-[100px]
        bg-white
        inner-shadow-blue
        rounded-xl
        px-6 py-5
        transition-all
        duration-500
        hover:bg-[linear-gradient(to_right,#082261,#34065c)]
        hover:text-white group"
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium">{label}</p>
          <p className="mt-1 text-2xl font-bold">{value}</p>
        </div>
        <Icon className="w-5 h-5 opacity-80 group-hover:text-white" />
      </div>
    </div>
  );
}

export default function FeedbacksPage() {
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState('');
  const debouncedSearch = useNewDebounce(search, 500);
  const [feedbackType, setFeedbackType] = useState<string>('all');
  const [selectedFeedback, setSelectedFeedback] = useState<Feedback | null>(null);

  const { data, isLoading, isFetching } = useGetFeedbacksQuery({
    feedbackType,
    skip: pageIndex * pageSize,
    take: pageSize,
    search: debouncedSearch || undefined,
  });

  useEffect(() => {
    setPageIndex(0);
  }, [debouncedSearch]);

  const feedbacks = useMemo(() => data?.data ?? [], [data]);
  const totalCount = data?.total ?? 0;

  const feedbackColumns: ColumnDef<Feedback>[] = [
    {
      id: 'sno',
      header: 'S. No.',
      cell: ({ row, table }) => {
        const { pageIndex, pageSize } = (table.options.meta as any)?.pagination ?? {
          pageIndex: 0,
          pageSize: 10,
        };
        return pageIndex * pageSize + row.index + 1;
      },
    },
    {
      id: 'name',
      accessorFn: (row) => `${row.email}_${row.name}`,
      header: 'Name',
      cell: ({ row }) => row.original.name || 'Guest',
      meta: {
        mergeRow: (_val: any, row: any) =>
          row.name && row.name.trim() !== '' && row.name.toLowerCase() !== 'guest',
      },
    },
    {
      id: 'email',
      accessorFn: (row) => `${row.email}_${row.name}`,
      header: 'Email',
      cell: ({ row }) => row.original.email || '-',
      meta: {
        mergeRow: (_val: any, row: any) =>
          row.email && row.email.trim() !== '' && row.email !== '-',
      },
    },
    // {
    //   accessorKey: 'contactNo',
    //   header: 'Contact No',
    //   cell: ({ row }) => row.original.contactNo || '-',
    // },
    // {
    //   accessorKey: 'type',
    //   header: 'Type',
    //   cell: ({ row }) => (
    //     <span className="px-2 py-1 text-xs font-medium text-blue-700 bg-blue-100 rounded-full">
    //       {row.original.type}
    //     </span>
    //   ),
    // },
    // {
    //   accessorKey: 'message',
    //   header: 'Message',
    //   cell: ({ row }) => (
    //     <div className="max-w-[200px] truncate" title={row.original.message}>
    //       {row.original.message}
    //     </div>
    //   ),
    // },

    {
      accessorKey: 'createdAt',
      header: 'Date',
      cell: ({ row }) => new Date(row.original.createdAt).toLocaleDateString(),
    },
    {
      id: 'actions',
      header: 'Feedback',
      cell: ({ row }) => (
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setSelectedFeedback(row.original)}
          className="hover:text-blue-600"
        >
          <Eye className="w-4 h-4" />
        </Button>
      ),
    },
  ];

  return (
    <div className="relative space-y-6">
      {(isLoading || isFetching) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/5 backdrop-blur-sm">
          <Loader />
        </div>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">User Feedbacks</h1>
          <p className="text-muted-foreground">View and manage feedbacks submitted by users.</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-6 mt-4">
        <StatCard
          label="Total Feedbacks"
          value={isLoading ? '—' : totalCount}
          icon={MessageSquare}
        />
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="grid gap-4 md:grid-cols-[1fr_auto]">
            <div className="space-y-2 relative max-w-[360px]">
              <Label htmlFor="search">Search Feedback</Label>
              <Search
                size={20}
                className="absolute -translate-y-1/2 right-3 top-[60%] text-muted-foreground"
              />
              <input
                id="search"
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name or email..."
                className="
                    w-full
                    border border-[#001B6B]
                    rounded-lg px-3 py-2 pr-10
                    outline-none
                    focus:ring-0
                  "
              />
            </div>

            {/* <div className="flex items-start justify-end gap-4">
              <div className="space-y-2 w-[140px]">
                <Label>Feedback Type</Label>
                <Select
                  value={feedbackType}
                  onValueChange={(val) => {
                    console.log('Selected:', val);
                    setFeedbackType(val);
                  }}
                >
                  <SelectTrigger
                    className="
                          w-full
                          border border-[#001B6B]
                          rounded-lg px-3 py-2
                          ring-0 focus:ring-0
                          shadow-none
                        "
                  >
                    <SelectValue placeholder="Select report type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Feedback</SelectItem>
                    <SelectItem value="opened">Opened</SelectItem>
                    <SelectItem value="submitted">Submitted</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div> */}
          </div>
        </CardContent>
      </Card>

      <div className="w-full font-lato">
        <DataTable
          data={feedbacks}
          columns={feedbackColumns as any}
          enableSearch={false}
          enableFilters={false}
          enableColumnVisibility={false}
          manualPagination={true}
          pageSize={pageSize}
          pageCount={Math.ceil(totalCount / pageSize)}
          onPaginationChange={({ pageIndex, pageSize }) => {
            setPageIndex(pageIndex);
            setPageSize(pageSize);
          }}
          isLoading={isLoading || isFetching}
        />
      </div>

      {/* Feedback Details Modal */}
      {selectedFeedback && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm !m-0">
          <div className="w-full max-w-2xl overflow-hidden duration-200 bg-white shadow-2xl rounded-xl animate-in fade-in zoom-in">
            <div className="flex items-center justify-between bg-[#001B6B] px-6 py-4 text-white">
              <h2 className="text-xl font-semibold font-spectral">Feedback Details</h2>
              <button
                onClick={() => setSelectedFeedback(null)}
                className="p-1 transition-colors rounded-lg hover:bg-white/20"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-blue-50">
                    <User className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-xs font-medium tracking-wider uppercase text-muted-foreground">
                      Name
                    </p>
                    <p className="text-sm font-semibold">{selectedFeedback.name || 'Guest'}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-blue-50">
                    <Mail className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-xs font-medium tracking-wider uppercase text-muted-foreground">
                      Email
                    </p>
                    <p className="text-sm font-semibold">{selectedFeedback.email || '-'}</p>
                  </div>
                </div>
              </div>
              {/* 
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-lg bg-blue-50">
                      <Phone className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-xs font-medium tracking-wider uppercase text-muted-foreground">
                        Contact No
                      </p>
                      <p className="text-sm font-semibold">{selectedFeedback.contactNo || '-'}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-lg bg-blue-50">
                      <Calendar className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-xs font-medium tracking-wider uppercase text-muted-foreground">
                        Submitted On
                      </p>
                      <p className="text-sm font-semibold">
                        {new Date(selectedFeedback.createdAt).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div> */}

              <div className="pt-4 border-t">
                <div className="flex items-center gap-2 mb-3">
                  <MessageSquare className="w-5 h-5 text-blue-600" />
                  <p className="text-sm font-semibold tracking-wider uppercase text-muted-foreground">
                    Message
                  </p>
                </div>
                <div className="p-4 overflow-y-auto text-sm italic leading-relaxed whitespace-pre-wrap border rounded-lg max-h-[220px] bg-muted/30">
                  "{selectedFeedback.message}"
                </div>
              </div>

              <div className="flex justify-end pt-4">
                <Button
                  onClick={() => setSelectedFeedback(null)}
                  className="bg-[#001B6B] hover:bg-[#001B6B]/90 text-white min-w-[100px]"
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
